import React, { useState, useEffect, useCallback } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import _ from "lodash";

// constant
import LocalStrings from "@language";
import { COMMON_MODAL_PAYLOAD } from "@constant";
import { REVIEWER_RESPONSE_TYPES } from "@reviewerconstant";
import { formUpFullName } from "@utilscommon";

// actions
import { fetchEventInterests, setCommonModal } from "@mainaction";
import { reviewerLogoutAction, fetchReviewerResponses } from "@revieweractions";

// components
import { renderSpinner } from "@basecomponent";
import Loader from "@loader";
import NavBar from "@reviewernavbar";
import ProposalReview from "@proposalreview";

const TAB_TYPES = REVIEWER_RESPONSE_TYPES;

const ReviewerDashboard = (props) => {
  const { session, interestList, reviewerResponses, commonModal } = props;

  const [selectedTab, setSelectedTab] = useState(TAB_TYPES.unReviewed);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (_.isEmpty(interestList))
      props.fetchEventInterests(session, (res) => {});
    handleListRefresh();
  }, []);

  const handleListRefresh = () => {
    setLoading(true);
    props.fetchReviewerResponses(session, (res) => {
      setLoading(false);
    });
  };

  const handleReviewerLogout = () => {
    props.reviewerLogoutAction(session, (res) => {});
  };

  const _renderResponses = useCallback(() => {
    const responseList = reviewerResponses[selectedTab];

    const handleConditionalColumn = (value) => {
      if (!_.isEmpty(value)) return value;
      else return " - ";
    };

    const handleReview = (value) => {
      let localPayload = _.cloneDeep(COMMON_MODAL_PAYLOAD);
      localPayload.state = true;
      localPayload.size = "lg";
      localPayload.data = value;
      props.setCommonModal(localPayload);
    };

    return (
      <>
        {!_.isEmpty(responseList) ? (
          _.map(responseList, (responseItem, responseKey) => {
            const showBorder = !_.isEqual(responseList.length, responseKey + 1);
            return (
              <div
                key={`${selectedTab}${responseKey}`}
                className="w-full grid grid-cols-10"
              >
                {/* ----- Abstract name ----- */}
                <div
                  className={`col-span-3 reviewer-response-table-body${
                    showBorder ? " border-b" : ""
                  }`}
                >
                  <span>
                    {handleConditionalColumn(
                      responseItem?.proposal_by_proposalidfk?.proposal_name
                    )}
                  </span>
                </div>
                {/* ----- Submitted by ----- */}
                <div
                  className={`col-span-3 reviewer-response-table-body${
                    showBorder ? " border-b" : ""
                  }`}
                >
                  <span>
                    {formUpFullName(
                      responseItem?.firstname,
                      responseItem?.lastname
                    )}
                  </span>
                </div>
                {/* ----- Category ----- */}
                <div
                  className={`col-span-2 reviewer-response-table-body${
                    showBorder ? " border-b" : ""
                  }`}
                >
                  <span>
                    {handleConditionalColumn(
                      responseItem?.proposal_by_proposalidfk?.category
                    )}
                  </span>
                </div>
                {/* ----- Action ----- */}
                <div
                  className={`col-span-2 reviewer-response-table-body${
                    showBorder ? " border-b" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="border rounded px-2 py-1 text-sm text-theme bg-white hover:shadow-lg"
                    onClick={() => handleReview(responseItem)}
                  >
                    {LocalStrings.static.kReview}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="w-full flex justify-center items-center py-4">
            <span className="text-sm text-gray-400">
              {LocalStrings.static.kNoAbstractResponses}
            </span>
          </div>
        )}
      </>
    );
  }, [reviewerResponses, selectedTab]);

  return (
    <article className="h-full w-full">
      {/* ----- Navbar ----- */}
      <NavBar logout={handleReviewerLogout} />

      {/* ----- Content body ----- */}
      <section className="bg-offwhite h-reviewerDash">
        <div className="max-w-screen-lg mx-auto py-10 flex flex-col gap-10 h-full w-full">
          {/* --- Label --- */}
          <div className="w-full flex justify-between items-center">
            <span className="text-base font-medium">
              {LocalStrings.static.kAbstractResponses}
            </span>

            {!_.isEmpty(reviewerResponses) && (
              <span
                className="text-sm text-theme cursor-pointer"
                onClick={handleListRefresh}
              >
                {loading ? renderSpinner(true) : LocalStrings.static.kRefresh}
              </span>
            )}
          </div>

          {/* --- Body --- */}
          <div className="max-h-full w-full bg-white rounded shadow-md p-3">
            {!_.isEmpty(reviewerResponses) ? (
              <>
                {/* - Tabs - */}
                <div className="h-8 flex items-center bg-white border-b">
                  <div
                    className={`${
                      _.isEqual(selectedTab, TAB_TYPES.unReviewed)
                        ? "border-b-4 -mb-1"
                        : "text-theme cursor-pointer hover:border-b -mb-[1px]"
                    } h-full min-w-36 flex justify-center items-center border-theme transition-all duration-100 ease-in-out`}
                    onClick={() => setSelectedTab(TAB_TYPES.unReviewed)}
                  >
                    {LocalStrings.static.kUnReviewed} (
                    {reviewerResponses?.unreviewed
                      ? reviewerResponses?.unreviewed?.length
                      : 0}
                    )
                  </div>
                  <div
                    className={`${
                      _.isEqual(selectedTab, TAB_TYPES.reviewed)
                        ? "border-b-4 -mb-1"
                        : "text-theme cursor-pointer hover:border-b -mb-[1px]"
                    } h-full min-w-36 flex justify-center items-center border-theme transition-all duration-100 ease-in-out`}
                    onClick={() => setSelectedTab(TAB_TYPES.reviewed)}
                  >
                    {LocalStrings.static.kReviewed} (
                    {reviewerResponses?.reviewed
                      ? reviewerResponses?.reviewed?.length
                      : 0}
                    )
                  </div>
                </div>

                {/* - Table - */}
                <div className="flex flex-col w-full mt-8">
                  <div className="w-full grid grid-cols-10">
                    {/* Header */}
                    <div className="col-span-3 reviewer-response-table-header">
                      <span>{LocalStrings.static.kAbstractName}</span>
                    </div>
                    <div className="col-span-3 reviewer-response-table-header">
                      <span>{LocalStrings.static.kSubmittedBy}</span>
                    </div>
                    <div className="col-span-2 reviewer-response-table-header">
                      <span>{LocalStrings.static.kCategory}</span>
                    </div>
                    <div className="col-span-2 reviewer-response-table-header">
                      <span>{LocalStrings.static.kAction}</span>
                    </div>
                  </div>

                  {/* Table body */}
                  {_renderResponses()}
                </div>
              </>
            ) : (
              <Loader />
            )}
          </div>
        </div>
      </section>

      {commonModal?.state && <ProposalReview listRefresh={handleListRefresh} />}
    </article>
  );
};

const mapStateToProps = (state) => {
  return {
    session: state.sessionReviewer,
    interestList: state.eventInterestsData,
    reviewerResponses: state.reviewerResponsesReducer,
    commonModal: state.commonModalReducer,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchEventInterests,
      setCommonModal,
      reviewerLogoutAction,
      fetchReviewerResponses,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(ReviewerDashboard);
